import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Backdrop,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useUser } from "../../../Services/ContexteUser";
import "../../../Styles/Create/Create.css";

/* --- Simple TabPanel helper --- */
function TabPanel({ index, value, children }) {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ width: "100%" }}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ManageBets = () => {
  const [bets, setBets] = useState(null);
  const [updatedBets, setUpdatedBets] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const { idBet } = useParams();
  const navigate = useNavigate();
  const { updateMonnaie } = useUser();

  // Dialog de clôture + toast
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    bet: null,
  });

  // Loadings
  const [loadingFetch, setLoadingFetch] = useState(true);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bettingToggleLoading, setBettingToggleLoading] = useState(false);
  const [statusToggleLoading, setStatusToggleLoading] = useState(false);
  const [distributingLoading, setDistributingLoading] = useState(false);

  // Feedback
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const idOrganisation = localStorage.getItem("idOrganisation");

  const getNormalizedStatus = (status) => {
    if (status === "terminé") return "terminé";
    if (status === "à venir") return "à venir";
    if (status === "En cours") return "En cours";
    return "En cours"; // compat
  };

  const mergeBet = (bet) => ({ ...bet, ...(updatedBets[bet.idBet] || {}) });
  const isTerminated = (bet) =>
    getNormalizedStatus(mergeBet(bet).matchStatus) === "terminé";

  // ---- Fetch / Refresh ----
  const fetchBet = useCallback(
    async (mode = "fetch") => {
      const path = `organisations/${idOrganisation}/bets`;
      try {
        mode === "refresh" ? setLoadingRefresh(true) : setLoadingFetch(true);
        const response = await fetch(
          `https://backend-betkiff.vercel.app/bets/getBetByID/${idBet}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path }),
          }
        );
        if (!response.ok)
          throw new Error(
            `Erreur : ${response.status} - ${response.statusText}`
          );
        const data = await response.json();
        setBets([{ idBet, ...data }]);
        setUpdatedBets({ [idBet]: { idBet, ...data } });
        setIsDirty(false);
      } catch (error) {
        console.error("Erreur lors de la récupération du pari :", error);
        setToast({
          open: true,
          message: "Impossible de rafraîchir les données.",
          severity: "error",
        });
      } finally {
        mode === "refresh" ? setLoadingRefresh(false) : setLoadingFetch(false);
      }
    },
    [idBet, idOrganisation]
  );

  useEffect(() => {
    fetchBet("fetch");
  }, [fetchBet]);

  // ---- Handlers ----
  const handleChange = (idBet, field, value) => {
    setUpdatedBets((prev) => ({
      ...prev,
      [idBet]: { ...prev[idBet], [field]: value },
    }));
    setIsDirty(true);
  };

  const handleChangeNested = (idBet, field, key, value) => {
    setUpdatedBets((prev) => {
      const nested = { ...(prev[idBet]?.[field] || {}) };
      nested[key] = value;
      return { ...prev, [idBet]: { ...prev[idBet], [field]: nested } };
    });
    setIsDirty(true);
  };

  // ---- Actions serveur + refresh auto ----
  const toggleBettingStatus = async (idBet) => {
    try {
      setBettingToggleLoading(true);
      await fetch(
        `https://backend-betkiff.vercel.app/bets/toggleBettingStatus/${idBet}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idOrganisation }),
        }
      );
      setToast({
        open: true,
        message: "Statut des mises mis à jour.",
        severity: "success",
      });
      await fetchBet("refresh");
    } catch (error) {
      console.error("Erreur toggleBettingStatus :", error);
      setToast({
        open: true,
        message: "Échec de mise à jour des mises.",
        severity: "error",
      });
    } finally {
      setBettingToggleLoading(false);
    }
  };

  const handleSubmitChanges = async () => {
    try {
      setSaving(true);
      await axios.put(
        `https://backend-betkiff.vercel.app/bets/updateBets/${idBet}`,
        {
          updatedData: updatedBets[idBet],
          idOrganisation,
        }
      );
      setToast({
        open: true,
        message: "Pari enregistré avec succès.",
        severity: "success",
      });
      await fetchBet("refresh");
    } catch (error) {
      console.error("Erreur updateBets :", error);
      setToast({
        open: true,
        message: "Échec de l'enregistrement.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const calculateWinning = async (bet, bettors) => {
    try {
      const response = await fetch(
        "https://backend-betkiff.vercel.app/bets/calculateWinningBet",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bet,
            bettors: bet.bettors,
            idBet: bet.idBet,
            idOrganisation,
          }),
        }
      );
      if (!response.ok)
        throw new Error(`Erreur : ${response.status} - ${response.statusText}`);
      const data = await response.json();
      return data.winningBettors;
    } catch (error) {
      console.error("Erreur calculateWinning :", error);
    }
  };

  const updateUserBalance = async (idUser, winning) => {
    try {
      const response = await fetch(
        `https://backend-betkiff.vercel.app/users/ById/${idUser}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idOrganisation }),
        }
      );
      if (!response.ok)
        throw new Error(`Erreur récupération utilisateur : ${response.status}`);
      const userData = await response.json();
      const newMoney = userData.nbMonnaie + winning;

      const updateResponse = await fetch(
        "https://backend-betkiff.vercel.app/users/updateMonnaie",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: idUser, newMoney, idOrganisation }),
        }
      );
      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        console.error("Erreur API mise à jour monnaie :", errorData);
        throw new Error(
          errorData.error || "Erreur lors de la mise à jour de l'argent."
        );
      }
      updateMonnaie(newMoney);
      await updateResponse.json();
    } catch (error) {
      console.error("Erreur dans updateUserBalance :", error.message);
    }
  };

  const distributeWinnings = async (bet) => {
    const merged = mergeBet(bet);
    try {
      setDistributingLoading(true);

      const updatedBet = {
        ...merged,
        matchStatus: "terminé",
        bettingOpen: false,
        distributeWinningDone: true,
      };

      // Optimiste local
      setUpdatedBets((prev) => ({ ...prev, [bet.idBet]: updatedBet }));
      setBets((prev) =>
        prev
          ? prev.map((b) =>
              b.idBet === bet.idBet ? { ...b, ...updatedBet } : b
            )
          : prev
      );

      await axios.put(
        `https://backend-betkiff.vercel.app/bets/updateBets/${bet.idBet}`,
        { updatedData: updatedBet, idOrganisation }
      );

      const winners = await calculateWinning(updatedBet, updatedBet.bettors);
      if (winners?.winningBettors?.length) {
        for (const { idUser, winnings } of winners.winningBettors) {
          await updateUserBalance(idUser, winnings);
        }
      }

      setToast({
        open: true,
        message: "Pari clôturé et gains distribués.",
        severity: "success",
      });
      await fetchBet("refresh");
    } catch (error) {
      console.error("Erreur distribution :", error);
      setToast({
        open: true,
        message: "Échec de la clôture.",
        severity: "error",
      });
    } finally {
      setDistributingLoading(false);
      setConfirmDialog({ open: false, bet: null });
    }
  };

  // Nouvelle version : ne permet que "à venir" -> "En cours"
  const startMatch = async (bet) => {
    const merged = mergeBet(bet);
    const normalized = getNormalizedStatus(merged.matchStatus);

    // Sécurité : si pas "à venir", on ne fait rien (le bouton n'est de toute façon pas affiché)
    if (normalized !== "à venir") return;

    const next = "En cours";

    try {
      setStatusToggleLoading(true);

      // Optimisme local
      setBets((prev) =>
        prev
          ? prev.map((b) =>
              b.idBet === bet.idBet ? { ...b, matchStatus: next } : b
            )
          : prev
      );
      setUpdatedBets((prev) => ({
        ...prev,
        [bet.idBet]: { ...merged, matchStatus: next },
      }));

      await axios.put(
        `https://backend-betkiff.vercel.app/bets/updateBets/${bet.idBet}`,
        { updatedData: { ...merged, matchStatus: next }, idOrganisation }
      );

      if (merged.bettingOpen) {
        setToast({
          open: true,
          message: "Astuce : pense à fermer les mises pendant le match.",
          severity: "info",
        });
      }

      await fetchBet("refresh");
    } catch (error) {
      console.error("Erreur startMatch :", error);
      setToast({
        open: true,
        message: "Impossible de démarrer le match.",
        severity: "error",
      });
      await fetchBet("refresh");
    } finally {
      setStatusToggleLoading(false);
    }
  };

  const handleTabChange = (_e, v) => setActiveTab(v);

  // --- Validation UI ---
  const validation = useMemo(() => {
    if (!bets) return {};
    const bet = bets[0];
    const merged = { ...bet, ...(updatedBets[bet.idBet] || {}) };
    const s1 = Number(merged?.score?.team1 ?? 0);
    const s2 = Number(merged?.score?.team2 ?? 0);
    const scoresOk =
      Number.isFinite(s1) && Number.isFinite(s2) && s1 >= 0 && s2 >= 0;

    const w1 = Number(merged?.odds?.winTeam1 ?? 0);
    const d = Number(merged?.odds?.draw ?? 0);
    const w2 = Number(merged?.odds?.winTeam2 ?? 0);
    const oddsOk = [w1, d, w2].every((x) => Number.isFinite(x) && x > 1);

    return { scoresOk, oddsOk };
  }, [bets, updatedBets]);

  // État initial
  if (loadingFetch || !bets) {
    return (
      <Box
        sx={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Chargement du pari…
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Références pour le header/footer (un seul bet)
  const headerBet = bets[0];
  const headerMerged = mergeBet(headerBet);
  const terminated = isTerminated(headerBet);
  const headerStatus = getNormalizedStatus(headerMerged.matchStatus);

  return (
    <div>
      {/* ===== Header ===== */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "rgb(255, 255, 255)",
          backdropFilter: "blur(6px)",
        }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            sx={{ color: "black" }}
            onClick={() => navigate("/")}>
            <ArrowBackIcon />
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ ml: 1, flexGrow: 1, overflow: "hidden" }}>
            <Typography
              noWrap
              variant="h6"
              sx={{ fontWeight: 700, color: "black" }}>
              {headerMerged.team1} vs {headerMerged.team2}
            </Typography>

            <Tooltip
              title={
                headerMerged.bettingOpen
                  ? "Les mises sont autorisées"
                  : "Les mises sont fermées"
              }>
              <Chip
                label={headerMerged.bettingOpen ? "Pari ouvert" : "Pari fermé"}
                color={headerMerged.bettingOpen ? "success" : "default"}
                size="small"
                sx={{
                  bgcolor: headerMerged.bettingOpen ? undefined : "grey.700",
                  color: "white",
                }}
              />
            </Tooltip>

            <Tooltip title={`Statut du match : ${headerStatus}`}>
              <Chip
                label={
                  headerStatus === "En cours"
                    ? "Match en cours"
                    : headerStatus === "à venir"
                      ? "Pas commencé"
                      : "Terminé"
                }
                color={
                  headerStatus === "En cours"
                    ? "warning"
                    : headerStatus === "à venir"
                      ? "default"
                      : "success"
                }
                size="small"
                sx={{ color: "white" }}
              />
            </Tooltip>
          </Stack>

          <Tooltip title="Rafraîchir les données">
            <span>
              <IconButton
                color="inherit"
                sx={{ color: "black" }}
                onClick={() => fetchBet("refresh")}
                disabled={loadingRefresh}>
                {loadingRefresh ? (
                  <CircularProgress size={18} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>

        {loadingRefresh && <LinearProgress color="secondary" />}

        <Toolbar sx={{ padding: 0, width: "100%", overflowX: "auto" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
                color: "black",
              },
              "& .MuiTabs-indicator": { height: 3 },
            }}>
            <Tab label="1. Score & Statut" sx={{ color: "black" }} />
            <Tab label="2. Côtes" sx={{ color: "black" }} />
            <Tab label="3. Infos du match" sx={{ color: "black" }} />
          </Tabs>
        </Toolbar>
      </AppBar>

      {/* ===== Contenu ===== */}
      <Container
        sx={{
          py: 3,
          pb: terminated ? 3 : 14 /* espace pour le footer fixé */,
        }}>
        {bets.map((bet) => {
          const merged = mergeBet(bet);
          const status = getNormalizedStatus(merged.matchStatus);

          return (
            <Paper
              key={bet.idBet}
              elevation={1}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}>
              {/* Alerte contexte */}

              {/* Onglet 1 : Score & Statut */}
              <TabPanel value={activeTab} index={0}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Score & Statut
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`Score ${merged.team1}`}
                      type="number"
                      inputProps={{
                        min: 0,
                        step: 1,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      fullWidth
                      disabled={terminated}
                      value={
                        // Laisser afficher '' pendant la saisie si présent dans le state
                        (updatedBets[bet.idBet]?.score?.team1 ??
                          bet.score.team1) === ""
                          ? ""
                          : (updatedBets[bet.idBet]?.score?.team1 ??
                            bet.score.team1)
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        // NE PAS convertir en nombre ici !
                        handleChangeNested(bet.idBet, "score", "team1", raw);
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        // À la sortie, on normalise : vide => 0, sinon clamp >= 0
                        const n =
                          raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0);
                        handleChangeNested(bet.idBet, "score", "team1", n);
                      }}
                      error={!validation.scoresOk}
                      helperText={
                        !validation.scoresOk
                          ? "Scores invalides : utilisez des entiers ≥ 0."
                          : " "
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={`Score ${merged.team2}`}
                      type="number"
                      inputProps={{
                        min: 0,
                        step: 1,
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                      }}
                      fullWidth
                      disabled={terminated}
                      value={
                        // Laisser afficher '' pendant la saisie si présent dans le state
                        (updatedBets[bet.idBet]?.score?.team2 ??
                          bet.score.team2) === ""
                          ? ""
                          : (updatedBets[bet.idBet]?.score?.team2 ??
                            bet.score.team2)
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        // NE PAS convertir en nombre ici !
                        handleChangeNested(bet.idBet, "score", "team2", raw);
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        // À la sortie, on normalise : vide => 0, sinon clamp >= 0
                        const n =
                          raw === "" ? 0 : Math.max(0, parseInt(raw, 10) || 0);
                        handleChangeNested(bet.idBet, "score", "team2", n);
                      }}
                      error={!validation.scoresOk}
                      helperText={
                        !validation.scoresOk
                          ? "Scores invalides : utilisez des entiers ≥ 0."
                          : " "
                      }
                    />
                  </Grid>

                  {/* Statut + bouton de bascule ICI (dans la section) */}
                  <Grid item xs={12}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems="center">
                      {!terminated && status === "à venir" && (
                        <Box sx={{ textAlign: "center", width: "100%" }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}>
                            ⚠️ Passer le match à <strong>« En cours »</strong>{" "}
                            est irréversible.
                          </Typography>

                          <Tooltip title='Passer en "En cours"'>
                            <span>
                              <Button
                                variant="outlined"
                                onClick={() => startMatch(bet)}
                                disabled={statusToggleLoading}>
                                {statusToggleLoading && (
                                  <CircularProgress size={18} sx={{ mr: 1 }} />
                                )}
                                Démarrer le match
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Onglet 2 : Côtes */}
              <TabPanel value={activeTab} index={1}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Côtes
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={`Victoire ${merged.team1}`}
                      type="number"
                      inputProps={{ min: 1.01, step: 0.01 }}
                      fullWidth
                      disabled={terminated}
                      value={
                        (updatedBets[bet.idBet]?.odds?.winTeam1 ??
                          bet.odds.winTeam1) === ""
                          ? ""
                          : (updatedBets[bet.idBet]?.odds?.winTeam1 ??
                            bet.odds.winTeam1)
                      }
                      onChange={(e) => {
                        handleChangeNested(
                          bet.idBet,
                          "odds",
                          "winTeam1",
                          e.target.value
                        ); // garder '' possible
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const n = raw === "" ? 0 : Math.max(0, parseFloat(raw));
                        handleChangeNested(
                          bet.idBet,
                          "odds",
                          "winTeam1",
                          Number.isFinite(n) ? n : 0
                        );
                      }}
                      error={!validation.oddsOk}
                      helperText={
                        !validation.oddsOk
                          ? "Chaque cote doit être > 1.00."
                          : " "
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Match nul"
                      type="number"
                      inputProps={{ min: 1.01, step: 0.01 }}
                      fullWidth
                      disabled={terminated}
                      value={
                        (updatedBets[bet.idBet]?.odds?.draw ??
                          bet.odds.draw) === ""
                          ? ""
                          : (updatedBets[bet.idBet]?.odds?.draw ??
                            bet.odds.draw)
                      }
                      onChange={(e) => {
                        handleChangeNested(
                          bet.idBet,
                          "odds",
                          "draw",
                          e.target.value
                        ); // garder '' possible
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const n = raw === "" ? 0 : Math.max(0, parseFloat(raw));
                        handleChangeNested(
                          bet.idBet,
                          "odds",
                          "draw",
                          Number.isFinite(n) ? n : 0
                        );
                      }}
                      error={!validation.oddsOk}
                      helperText={
                        !validation.oddsOk
                          ? "Chaque cote doit être > 1.00."
                          : " "
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={`Victoire ${merged.team2}`}
                      type="number"
                      inputProps={{ min: 1.01, step: 0.01 }}
                      fullWidth
                      disabled={terminated}
                      value={
                        (updatedBets[bet.idBet]?.odds?.winTeam2 ??
                          bet.odds.winTeam2) === ""
                          ? ""
                          : (updatedBets[bet.idBet]?.odds?.winTeam2 ??
                            bet.odds.winTeam2)
                      }
                      onChange={(e) => {
                        handleChangeNested(
                          bet.idBet,
                          "odds",
                          "winTeam2",
                          e.target.value
                        ); // garder '' possible
                      }}
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        const n = raw === "" ? 0 : Math.max(0, parseFloat(raw));
                        handleChangeNested(
                          bet.idBet,
                          "odds",
                          "winTeam2",
                          Number.isFinite(n) ? n : 0
                        );
                      }}
                      error={!validation.oddsOk}
                      helperText={
                        !validation.oddsOk
                          ? "Chaque cote doit être > 1.00."
                          : " "
                      }
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Onglet 3 : Infos du match */}
              <TabPanel value={activeTab} index={2}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Informations du match
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Équipe 1"
                      fullWidth
                      disabled={terminated}
                      value={updatedBets[bet.idBet]?.team1 ?? bet.team1}
                      onChange={(e) =>
                        handleChange(bet.idBet, "team1", e.target.value)
                      }
                      helperText="Nom complet recommandé."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Équipe 2"
                      fullWidth
                      disabled={terminated}
                      value={updatedBets[bet.idBet]?.team2 ?? bet.team2}
                      onChange={(e) =>
                        handleChange(bet.idBet, "team2", e.target.value)
                      }
                      helperText="Nom complet recommandé."
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            </Paper>
          );
        })}
      </Container>

      {/* ===== Footer (sans bouton statut) ===== */}
      {!terminated && (
        <Paper
          component="footer"
          elevation={3}
          square
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            zIndex: (t) => t.zIndex.drawer + 2,

            // Padding vertical par défaut (fallback desktop / navigateurs sans safe-area)
            py: 1.25,
            paddingBottom: 1.25,

            /* iOS 11.x (ancien Safari) */
            "@supports (padding-bottom: constant(safe-area-inset-bottom))": {
              paddingBottom: "calc(12px + constant(safe-area-inset-bottom))",
            },

            /* iOS 12+ (Safari moderne) */
            "@supports (padding-bottom: env(safe-area-inset-bottom))": {
              paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
            },
          }}>
          <Container>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              justifyContent="flex-end"
              alignItems={{ xs: "stretch", sm: "center" }}>
              {/* Ouvrir / Fermer les mises */}
              <Tooltip
                title={
                  headerMerged.bettingOpen
                    ? "Fermer les mises"
                    : "Réouvrir les mises"
                }>
                <span>
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => toggleBettingStatus(headerBet.idBet)}
                    disabled={bettingToggleLoading}>
                    {bettingToggleLoading && (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    )}
                    {headerMerged.bettingOpen
                      ? "Fermer les mises"
                      : "Ouvrir les mises"}
                  </Button>
                </span>
              </Tooltip>

              {/* Enregistrer */}
              <Tooltip title="Enregistrer les modifications sans clôturer le pari">
                <span>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleSubmitChanges}
                    disabled={!isDirty || saving}>
                    {saving && <CircularProgress size={18} sx={{ mr: 1 }} />}
                    Enregistrer
                  </Button>
                </span>
              </Tooltip>

              {/* Clôturer & distribuer */}
              <Tooltip title="Clôture définitive + distribution des gains">
                <span>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    disabled={
                      distributingLoading ||
                      headerMerged.distributeWinningDone ||
                      !validation.scoresOk
                    }
                    onClick={() =>
                      setConfirmDialog({ open: true, bet: headerBet })
                    }>
                    {distributingLoading && (
                      <CircularProgress size={18} sx={{ mr: 1 }} />
                    )}
                    Clôturer & distribuer
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Container>
        </Paper>
      )}

      {/* Dialog de confirmation clôture */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, bet: null })}
        fullWidth
        maxWidth="sm">
        {confirmDialog.bet && (
          <>
            <DialogTitle>Clôturer ce pari ?</DialogTitle>
            <DialogContent dividers>
              <Typography sx={{ mb: 2 }}>
                Cette action est <strong>définitive</strong> : le pari sera{" "}
                <strong>clôturé</strong> et les{" "}
                <strong>gains seront distribués</strong>.
              </Typography>

              {(() => {
                const bet = confirmDialog.bet;
                const merged = mergeBet(bet);
                const s1 = Number(merged?.score?.team1 ?? bet.score.team1 ?? 0);
                const s2 = Number(merged?.score?.team2 ?? bet.score.team2 ?? 0);
                const winKey = s1 > s2 ? "team1" : s2 > s1 ? "team2" : "draw";
                const style = (who) =>
                  winKey === who ? { fontWeight: 700, color: "green" } : {};
                return (
                  <Box
                    sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={8}>
                        <Typography sx={style("team1")}>
                          {merged.team1}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography sx={style("team1")} align="right">
                          {s1}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography sx={style("team2")}>
                          {merged.team2}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography sx={style("team2")} align="right">
                          {s2}
                        </Typography>
                      </Grid>
                    </Grid>
                    {winKey === "draw" && (
                      <Typography sx={{ mt: 1 }} variant="body2">
                        Match nul — la distribution suivra la logique côté
                        serveur.
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setConfirmDialog({ open: false, bet: null })}>
                Annuler
              </Button>
              <Button
                variant="contained"
                color="success"
                disabled={distributingLoading}
                onClick={() => distributeWinnings(confirmDialog.bet)}>
                {distributingLoading && (
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                )}
                Confirmer la clôture
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Backdrop pour les opérations longues (clôture) */}
      <Backdrop
        open={distributingLoading}
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.modal + 1 }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress color="inherit" />
          <Typography>Distribution des gains…</Typography>
        </Stack>
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManageBets;
